import { useState, useCallback, useEffect } from 'react';
import { cohortsApi } from '../../../services/api/cohorts';
import type { HealthSummaryResponse } from '../../../types';

interface UseHealthSummaryOptions {
  cohortId: string;
  enabled?: boolean;
}

interface UseHealthSummaryResult {
  healthSummary: HealthSummaryResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useHealthSummary({
  cohortId,
  enabled = true,
}: UseHealthSummaryOptions): UseHealthSummaryResult {
  const [healthSummary, setHealthSummary] = useState<HealthSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHealthSummary = useCallback(async () => {
    if (!enabled || !cohortId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await cohortsApi.getHealthSummary(cohortId);
      setHealthSummary(data);
    } catch {
      setError('Failed to load health summary');
    } finally {
      setLoading(false);
    }
  }, [cohortId, enabled]);

  useEffect(() => {
    fetchHealthSummary();
  }, [fetchHealthSummary]);

  return { healthSummary, loading, error, refresh: fetchHealthSummary };
}
